import {createSmokingSession,SmokingSessionStatus} from '../journal/session.js';
import {createSmokingItem} from '../journal/item.js';
import {createItemPreparation} from '../journal/preparation.js';
import {createSmokingStage} from '../journal/stage.js';
import {createStageTarget,createStageItemTarget} from '../journal/stage-target.js';
import {createSessionWood} from '../journal/wood-mix.js';

function nextId(idFactory,kind,sourceId,repeatRequestId){
  if(typeof idFactory!=='function')throw new TypeError('idFactory is required');
  const id=String(idFactory(kind,sourceId,repeatRequestId)||'').trim();
  if(!id)throw new TypeError('idFactory must return an id');
  return id;
}

export function duplicateCompletedSessionPlan({
  sourceSession,
  items=[],
  preparations=[],
  stages=[],
  stageTargets=[],
  stageItemTargets=[],
  woods=[],
  idFactory,
  repeatRequestId,
  plannedStartAt=null,
  plannedEndAt=null
}={}){
  const source=createSmokingSession(sourceSession);
  const requestId=String(repeatRequestId||'').trim();
  if(!requestId)throw new TypeError('repeatRequestId is required for idempotent retries');
  if(source.status!==SmokingSessionStatus.COMPLETED){
    throw new TypeError('REPEAT_SOURCE_MUST_BE_COMPLETED');
  }

  const sessionId=nextId(idFactory,'session',source.id,requestId);
  const session=createSmokingSession({
    id:sessionId,
    userId:source.userId,
    status:SmokingSessionStatus.DRAFT,
    title:source.title,
    smokerId:source.smokerId,
    smokerSnapshot:source.smokerSnapshot,
    recipeId:source.recipeId,
    sourceSessionId:source.id,
    plannedStartAt,
    plannedEndAt
  });

  const itemIdMap=new Map();
  const copiedItems=items.filter(item=>!item.deletedAt).map(raw=>{
    const item=createSmokingItem(raw);
    if(item.sessionId!==source.id)throw new TypeError('ITEM_SOURCE_SESSION_MISMATCH');
    const id=nextId(idFactory,'item',item.id,requestId);
    itemIdMap.set(item.id,id);
    return createSmokingItem({...item,id,userId:source.userId,sessionId,deletedAt:null});
  });

  const stageIdMap=new Map();
  const copiedStages=stages.filter(stage=>!stage.deletedAt).map(raw=>{
    const stage=createSmokingStage(raw);
    if(stage.sessionId!==source.id)throw new TypeError('STAGE_SOURCE_SESSION_MISMATCH');
    const id=nextId(idFactory,'stage',stage.id,requestId);
    stageIdMap.set(stage.id,id);
    return createSmokingStage({...stage,id,userId:source.userId,sessionId,deletedAt:null});
  });

  const copiedPreparations=preparations.map(raw=>{
    const preparation=createItemPreparation(raw);
    const itemId=itemIdMap.get(preparation.itemId);
    if(!itemId)throw new TypeError('PREPARATION_SOURCE_ITEM_MISMATCH');
    return createItemPreparation({...preparation,itemId,userId:source.userId});
  });

  const copiedWoods=woods.filter(wood=>!wood.deletedAt).map(raw=>{
    const wood=createSessionWood(raw);
    if(wood.sessionId!==source.id)throw new TypeError('WOOD_SOURCE_SESSION_MISMATCH');
    return createSessionWood({
      ...wood,
      id:nextId(idFactory,'wood',wood.id,requestId),
      userId:source.userId,
      sessionId,
      deletedAt:null
    });
  });

  const copiedStageTargets=stageTargets.map(raw=>{
    const target=createStageTarget(raw);
    const stageId=stageIdMap.get(target.stageId);
    if(!stageId)throw new TypeError('STAGE_TARGET_SOURCE_MISMATCH');
    return createStageTarget({...target,stageId,userId:source.userId});
  });

  const copiedStageItemTargets=stageItemTargets.map(raw=>{
    const target=createStageItemTarget(raw);
    const stageId=stageIdMap.get(target.stageId);
    const itemId=itemIdMap.get(target.itemId);
    if(!stageId||!itemId)throw new TypeError('STAGE_ITEM_TARGET_SOURCE_MISMATCH');
    return createStageItemTarget({...target,stageId,itemId,userId:source.userId});
  });

  return Object.freeze({
    repeatRequestId:requestId,
    session,
    items:Object.freeze(copiedItems),
    preparations:Object.freeze(copiedPreparations),
    stages:Object.freeze(copiedStages),
    stageTargets:Object.freeze(copiedStageTargets),
    stageItemTargets:Object.freeze(copiedStageItemTargets),
    woods:Object.freeze(copiedWoods)
  });
}
